import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useTranslations } from '../../hooks/useTranslations';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  UserGroupIcon,
  CurrencyDollarIcon,
  AcademicCapIcon,
  ChartBarIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const COLORS = ['#259584', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'];

function Dashboard() {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeFilter, setTimeFilter] = useState('month'); // week, month, year
  const { getAuthHeader } = useAdminAuth();
  const { t } = useTranslations();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('https://api.maxprofitacademy.com/admin/user', {
        method: 'GET',
        headers: getAuthHeader()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const result = await response.json();
      
      if (result.status === 200 && Array.isArray(result.data)) {
        setUsers(result.data);
      } else {
        throw new Error(t('users.invalidDataFormat'));
      }
    } catch (err) {
      setError(t('users.errorLoadingUsers'));
      console.error('Error loading users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Фильтрация по времени
  const getFilteredUsers = () => {
    const now = new Date();
    const filterDate = new Date();

    switch (timeFilter) {
      case 'week':
        filterDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        filterDate.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        filterDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        filterDate.setMonth(now.getMonth() - 1);
    }

    return users.filter(user => {
      const createdAt = new Date(user.created_at);
      return createdAt >= filterDate;
    });
  };

  const filteredUsers = getFilteredUsers();

  // Статистика
  const stats = {
    totalUsers: users.length,
    newUsers: filteredUsers.length,
    totalBalance: users.reduce((sum, user) => sum + Number(user.balance || 0), 0),
    averageBalance: users.length > 0 ? (users.reduce((sum, user) => sum + Number(user.balance || 0), 0) / users.length).toFixed(2) : 0,
    withCourse: users.filter(user => user.course && user.course !== 'NONE').length,
    withPromotion: users.filter(user => user.promotion).length,
    withCode: users.filter(user => user.code && user.code.trim() !== '').length,
    courses: {
      NONE: users.filter(user => !user.course || user.course === 'NONE').length,
      SILVER: users.filter(user => user.course === 'SILVER').length,
      GOLD: users.filter(user => user.course === 'GOLD').length,
      PLATINUM: users.filter(user => user.course === 'PLATINUM').length
    }
  };

  // Данные для графика регистраций по дням
  const getRegistrationData = () => {
    const data = {};
    const now = new Date();
    
    filteredUsers.forEach(user => {
      const date = new Date(user.created_at);
      const dateKey = date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
      data[dateKey] = (data[dateKey] || 0) + 1;
    });

    return Object.entries(data)
      .sort((a, b) => {
        const [dayA, monthA] = a[0].split('.');
        const [dayB, monthB] = b[0].split('.');
        return new Date(2024, monthA - 1, dayA) - new Date(2024, monthB - 1, dayB);
      })
      .map(([date, count]) => ({
        date,
        registrations: count
      }));
  };

  // Данные для графика курсов (используем filteredUsers для учета временного фильтра)
  const getCourseData = () => {
    const courseStats = {
      NONE: filteredUsers.filter(user => !user.course || user.course === 'NONE').length,
      SILVER: filteredUsers.filter(user => user.course === 'SILVER').length,
      GOLD: filteredUsers.filter(user => user.course === 'GOLD').length,
      PLATINUM: filteredUsers.filter(user => user.course === 'PLATINUM').length
    };
    
    return [
      { name: t('users.noCourse'), value: courseStats.NONE, color: COLORS[0] },
      { name: 'SILVER', value: courseStats.SILVER, color: COLORS[1] },
      { name: 'GOLD', value: courseStats.GOLD, color: COLORS[2] },
      { name: 'PLATINUM', value: courseStats.PLATINUM, color: COLORS[3] }
    ].filter(item => item.value > 0);
  };

  // Данные для графика баланса по курсам (используем filteredUsers для учета временного фильтра)
  const getBalanceByCourse = () => {
    const balances = {
      NONE: 0,
      SILVER: 0,
      GOLD: 0,
      PLATINUM: 0
    };

    filteredUsers.forEach(user => {
      const course = user.course || 'NONE';
      balances[course] = (balances[course] || 0) + Number(user.balance || 0);
    });

    return [
      { course: t('users.noCourse'), balance: balances.NONE },
      { course: 'SILVER', balance: balances.SILVER },
      { course: 'GOLD', balance: balances.GOLD },
      { course: 'PLATINUM', balance: balances.PLATINUM }
    ];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Filter */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('adminDashboard.title') || 'Дашборд'}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('adminDashboard.subtitle') || 'Обзор статистики платформы'}</p>
        </div>
        
        <div className="flex items-center space-x-2 bg-white rounded-lg border border-gray-200 p-1">
          <button
            onClick={() => setTimeFilter('week')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              timeFilter === 'week' ? 'bg-primary text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('adminDashboard.week') || 'Неделя'}
          </button>
          <button
            onClick={() => setTimeFilter('month')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              timeFilter === 'month' ? 'bg-primary text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('adminDashboard.month') || 'Месяц'}
          </button>
          <button
            onClick={() => setTimeFilter('year')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              timeFilter === 'year' ? 'bg-primary text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('adminDashboard.year') || 'Год'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('adminDashboard.totalUsers') || 'Всего пользователей'}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalUsers}</p>
              <p className="text-xs text-green-600 mt-1">+{stats.newUsers} за период</p>
            </div>
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
              <UserGroupIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('adminDashboard.totalBalance') || 'Общий баланс'}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">${stats.totalBalance}</p>
              <p className="text-xs text-gray-500 mt-1">~${stats.averageBalance} в среднем</p>
            </div>
            <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
              <CurrencyDollarIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('adminDashboard.withCourse') || 'Купили курс'}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.withCourse}</p>
              <p className="text-xs text-gray-500 mt-1">
                {stats.totalUsers > 0 ? ((stats.withCourse / stats.totalUsers) * 100).toFixed(1) : 0}% конверсия
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
              <AcademicCapIcon className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('adminDashboard.promotions') || 'Активные акции'}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.withPromotion}</p>
              <p className="text-xs text-gray-500 mt-1">{stats.withCode} с промо-кодом</p>
            </div>
            <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
              <ChartBarIcon className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Registrations Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('adminDashboard.registrations') || 'Регистрации по дням'}
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={getRegistrationData()}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: '12px' }} />
              <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
              />
              <Bar dataKey="registrations" fill="#259584" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Course Distribution Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('adminDashboard.courseDistribution') || 'Распределение по курсам'}
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={getCourseData()}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {getCourseData().map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h4 className="text-sm font-medium text-gray-600 mb-3">{t('adminDashboard.courseBreakdown') || 'Детализация курсов'}</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">SILVER</span>
              <span className="text-sm font-bold text-gray-900">{stats.courses.SILVER}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">GOLD</span>
              <span className="text-sm font-bold text-gray-900">{stats.courses.GOLD}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">PLATINUM</span>
              <span className="text-sm font-bold text-gray-900">{stats.courses.PLATINUM}</span>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-gray-200">
              <span className="text-sm text-gray-700">{t('users.noCourse')}</span>
              <span className="text-sm font-bold text-gray-900">{stats.courses.NONE}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h4 className="text-sm font-medium text-gray-600 mb-3">{t('adminDashboard.promotionStats') || 'Статистика акций'}</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">{t('adminDashboard.withPromotion') || 'С promotion'}</span>
              <span className="text-sm font-bold text-green-600">{stats.withPromotion}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">{t('adminDashboard.withPromoCode') || 'С промо-кодом'}</span>
              <span className="text-sm font-bold text-purple-600">{stats.withCode}</span>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-gray-200">
              <span className="text-sm text-gray-700">{t('adminDashboard.conversionRate') || 'Конверсия'}</span>
              <span className="text-sm font-bold text-blue-600">
                {stats.totalUsers > 0 ? ((stats.withCourse / stats.totalUsers) * 100).toFixed(1) : 0}%
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h4 className="text-sm font-medium text-gray-600 mb-3">{t('adminDashboard.recentActivity') || 'Недавняя активность'}</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">{t('adminDashboard.newRegistrations') || 'Новые регистрации'}</span>
              <span className="text-sm font-bold text-gray-900">{stats.newUsers}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">{t('adminDashboard.period') || 'Период'}</span>
              <span className="text-sm font-bold text-gray-900">
                {timeFilter === 'week' && (t('adminDashboard.lastWeek') || 'За неделю')}
                {timeFilter === 'month' && (t('adminDashboard.lastMonth') || 'За месяц')}
                {timeFilter === 'year' && (t('adminDashboard.lastYear') || 'За год')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
